import { SectionPaymentMethod } from '../types'

/**
 * How many assignments a section may publish (BL-5134).
 *
 * A section pass covers up to `sectionPassQuantity` assignments. CX can grant
 * exceptions on top with `sectionPassExtraAssignments`. Publishing an
 * assignment is blocked once the section already has that many active
 * assignments; cancelling one frees its slot. Drafts and unconfigured LTI
 * assignments do not count until they are published.
 *
 * The limit applies to sections that sell a section pass (payment methods
 * 1-4, codeless included) and to sections flagged `invoiced`, where the
 * institution is billed per seat for up to that many assignments. Other pay
 * per assignment sections, and sections whose quantity is unset, -1
 * (unlimited) or 0, have no limit.
 */
export type AssignmentLimit =
  | { limited: false }
  | {
      limited: true
      /** The pass's max assignments. */
      quantity: number
      /** Extra assignments CX allowed on top of the pass. */
      extra: number
      /** quantity + extra */
      limit: number
    }

const PASS_METHODS = new Set<SectionPaymentMethod>([
  SectionPaymentMethod.sectionPassStripe,
  SectionPaymentMethod.sectionPassCoupon,
  SectionPaymentMethod.sectionPassStripeCoupon,
  SectionPaymentMethod.sectionPassCodelessAccess,
])

export function getAssignmentLimit(section: {
  sectionPaymentMethod?: SectionPaymentMethod | null
  sectionPassQuantity?: number | null
  sectionPassExtraAssignments?: number | null
  invoiced?: boolean | null
}): AssignmentLimit {
  const method = section.sectionPaymentMethod ?? SectionPaymentMethod.token
  const quantity = section.sectionPassQuantity
  if (!PASS_METHODS.has(method) && section.invoiced !== true) {
    return { limited: false }
  }
  if (quantity === undefined || quantity === null || quantity <= 0) {
    return { limited: false }
  }
  const extra = Math.max(
    0,
    Math.floor(section.sectionPassExtraAssignments ?? 0)
  )
  return { limited: true, quantity, extra, limit: quantity + extra }
}

/** Slots left, or null when the section has no limit. Never negative. */
export function assignmentsRemaining(
  limit: AssignmentLimit,
  activeAssignmentCount: number
): number | null {
  if (!limit.limited) return null
  return Math.max(0, limit.limit - activeAssignmentCount)
}

/** Whether `adding` more assignments can be published. */
export function canPublishAssignments(
  limit: AssignmentLimit,
  activeAssignmentCount: number,
  adding = 1
): boolean {
  if (!limit.limited) return true
  return activeAssignmentCount + adding <= limit.limit
}

/** Thrown when publishing would go past the section's assignment limit. */
export class AssignmentLimitReachedError extends Error {
  readonly limit: number
  readonly active: number
  readonly adding: number

  constructor({
    limit,
    active,
    adding,
  }: {
    limit: number
    active: number
    adding: number
  }) {
    super(
      `Assignment limit reached: ${active} of ${limit} published, ${adding} more requested`
    )
    this.name = 'AssignmentLimitReachedError'
    this.limit = limit
    this.active = active
    this.adding = adding
  }
}

export function isAssignmentLimitReachedError(
  error: unknown
): error is AssignmentLimitReachedError {
  return (
    error instanceof AssignmentLimitReachedError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as { name?: unknown }).name === 'AssignmentLimitReachedError')
  )
}
